import { PageSection, PageSectionContent } from '@greystar/designsystem';
import {
  BreadcrumbArgs,
  buildBreadcrumb,
  withBooleanFeatureFlag,
  Property,
  reduceAvailableUnits,
  deleteUndefined,
  getSiteBaseUrl,
  GetLevelsResponse,
  getLocationGuideLink,
} from '@greystar/lib';
import { PropertyDetailsBreadcrumbProps, BreadcrumbNavigation } from '@greystar/ui';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { JSX } from 'react';
import { useRouter } from 'next/router';

const PropertyDetailsBreadcrumb = (props: PropertyDetailsBreadcrumbProps): JSX.Element => {
  const router = useRouter();
  if (!props.data?.breadcrumb) {
    return <></>;
  }

  const lastBreadcrumb = props.data.breadcrumb[props.data.breadcrumb.length - 1];
  const shouldAddPropertyName = lastBreadcrumb?.text !== props.data.property?.name;

  const breadcrumbsWithProperty = shouldAddPropertyName
    ? [
        ...props.data.breadcrumb,
        {
          text: props.data.property?.name ?? '',
          href: router.asPath.split('/calculator')[0],
        },
      ]
    : props.data.breadcrumb;

  const unitId =
    (router.query.unitId as string) ?? props.data?.property?.availableUnits?.[0]?.unitId ?? '';
  const unitNumber = router.asPath.includes('calculator')
    ? props.data.property?.availableUnits?.find((unit) => unit.unitId === unitId)?.unitNumber
    : null;

  const breadcrumbs = unitNumber
    ? [
        ...breadcrumbsWithProperty,
        {
          text: unitNumber,
          href: router.asPath,
          isActive: false,
        },
      ]
    : breadcrumbsWithProperty;

  return (
    <PageSection isBottomFlushed="true" isTopFlushed="true">
      <PageSectionContent>
        <BreadcrumbNavigation links={breadcrumbs} />
      </PageSectionContent>
    </PageSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsBreadcrumbProps> => {
  const props = rendering as unknown as PropertyDetailsBreadcrumbProps;
  const property = layoutData.sitecore.context.property as Property;

  if (!property) {
    return {
      ...props,
      data: {
        breadcrumb: null,
        notFound: true,
        property: null,
      },
    };
  }

  const locationsData = layoutData.sitecore.context.locationsData as GetLevelsResponse;

  let neighborhoodLink: string | null = null;
  let neighborhoodText: string | null = null;

  if (locationsData?.search?.results?.length) {
    if (property?.location?.greystarNeighborhood) {
      const neighborhoodData = locationsData.search.results.find(
        (result) =>
          result.neighborhoodName &&
          result.neighborhoodName.value.toLowerCase() ===
            property.location?.greystarNeighborhood?.toLowerCase()
      );

      if (neighborhoodData) {
        neighborhoodLink = neighborhoodData.url?.path
          ? getLocationGuideLink(neighborhoodData.url.path)
          : null;

        neighborhoodText = neighborhoodData.neighborhoodName
          ? neighborhoodData.neighborhoodName.value
          : null;
      }
    }

    const shouldTryMarketArea =
      property?.location?.greystarMarketArea && (!neighborhoodLink || !neighborhoodText);

    if (shouldTryMarketArea) {
      const marketAreaData = locationsData.search.results.find(
        (result) =>
          result.marketAreaName &&
          result.marketAreaName.value.toLowerCase() ===
            property.location?.greystarMarketArea?.toLowerCase()
      );

      if (marketAreaData) {
        neighborhoodLink = marketAreaData.url?.path
          ? getLocationGuideLink(marketAreaData.url.path)
          : null;

        neighborhoodText = marketAreaData.marketAreaName
          ? marketAreaData.marketAreaName.value
          : null;
      }
    }
  }

  const breadcrumbArgs: BreadcrumbArgs | null = props.fields
    ? {
        segmentTokens: props.fields.template.value.split('|'),
        homeText: props.fields.homeText.value,
        homeHost: getSiteBaseUrl('Editorial'),
        searchHost: getSiteBaseUrl('Search'),
        pdpHost: getSiteBaseUrl('PDP'),
        includeHome: props.fields.includeHome.value,
        neighborhoodLink,
        neighborhoodText,
      }
    : null;

  const breadcrumb = breadcrumbArgs ? await buildBreadcrumb(property, breadcrumbArgs) : null;

  return {
    ...props,
    data: {
      breadcrumb,
      notFound: false,
      property: deleteUndefined<Property>({
        id: property.id,
        name: property.name,
        availableUnits: reduceAvailableUnits({
          units: property.availableUnits,
          removeRentMatrix: true,
          removePrices: true,
        }),
      }),
    },
  };
};

export default withDatasourceCheck()<PropertyDetailsBreadcrumbProps>(
  withBooleanFeatureFlag(PropertyDetailsBreadcrumb)
);
